import { ExternalTransactionTypes } from '../enum/extranal-transaction-types';
import { TransactionHoldReason } from '../enum/transaction-hold-reason';
import { TransactionStatus } from '../enum/transaction-status';
import { TransactionAuthAmountTypeEnum } from '../enum/transaction-auth-amount-type';
import { TransactionCategorizationType } from '../enum/transaction-categorization-type';
import { TransactionAllowCategorizationEnum } from '../enum/transaction-allow-categorization';
import { TransactionCategoryDetails } from './transaction-category-details.interface';
import { TransactionLocationDetails } from './transaction-location-details';
import { TransactionCheckDetails } from './transaction-check-details.interface';
import { convertValueToUSD } from '../../../helpers/currency.helper';
import { validateFeatureFlag } from '../../../services/featureFlagService';
import { FEATURE_FLAG } from '../../../constants/general';

export enum TransactionExternalAccountType {
  PLAID = 'PLAID',
}

export interface LiliTransaction {
  creditCard: boolean;
  activityTypeCode: string;
  timestamp: string | Date;
  pending: boolean;
  type: string;
  mccCode?: string;
  sourceId: string;
  settleCurrencyCode: string;
  billingAmount?: number;
  billingCurrencyCode: string;
  allowCategorizationEnum: string;
  trxType: string;
  trxTypeParams: string;
  onHold: boolean;
  initiatorPan?: string;
  initiatorFirstName?: string;
  initiatorLastName?: string;
  transactionId: string;
  externalTransactionType: ExternalTransactionTypes;
  amount: number;
  currencyCode: string;
  localCurrencyCode?: string;
  localAmount?: number;
  title?: string;
  merchantName?: string;
  billerName?: string;
  prettyName?: string;
  details?: string;
  authDelta?: number;
  authAmountType?: TransactionAuthAmountTypeEnum;
  transactionStatus?: TransactionStatus;
  holdReason?: TransactionHoldReason;
  holdText?: string;
  comment?: string;
  allowComment?: boolean;
  categorizationType: TransactionCategorizationType;
  categorizationAvailability?: TransactionAllowCategorizationEnum;
  suggestedTaxCategoryId?: string;
  categoryDetails?: TransactionCategoryDetails;
  receiptKey?: string;
  locationKey?: string;
  merchantLogoUrl?: string;
  locationDetails?: TransactionLocationDetails;
  expectedClearDate?: Date | string;
  sourceAchNumber?: string;
  sourceRoutingNumber?: string;
  targetAchNumber?: string;
  targetRoutingNumber?: string;
  bucketAccountId?: string;
  checkDetailsData?: TransactionCheckDetails;
  isLast?: boolean;
  found?: boolean;
  id?: string;
  date?: string;
  name?: string;
  category?: string;
  status?: string;
  effectiveDate?: string;
  externalAccountType?: TransactionExternalAccountType;
  imadNumber?: string;
  instructedAmountCurrencyCode?: number;
  swiftCode?: string;
  iban?: string;
  uetr?: string;
  targetWireAccountNumber?: string;
  targetWireRoutingNumber?: string;
  additionalPaymentInformation?: string;
  [key: string]: any;
}

export function prettyTitle(t: LiliTransaction): string {
  return t.title || t.merchantName || t.billerName || t.prettyName || t.details || '';
}

export function formattedAmount(t: LiliTransaction): string {
  return convertValueToUSD(t.amount, 2);
}

export function formattedDate(t: LiliTransaction): string {
  return new Date(t.timestamp).toISOString();
}

export function formattedBusinessAmount(t: LiliTransaction): string {
  if ((t.categoryDetails?.businessPercent ?? 0) > 0) {
    // @ts-ignore
    return convertValueToUSD(t.amount * t.categoryDetails!.businessPercent, 2);
  }
  return '';
}

export function positiveFormattedAmount(t: LiliTransaction): string {
  return convertValueToUSD(Math.abs(t.amount), 2);
}

export function positiveFormattedBusinessAmount(t: LiliTransaction): string {
  if ((t.categoryDetails?.businessPercent ?? 0) > 0) {
    // @ts-ignore
    return convertValueToUSD(Math.abs(t.amount * t.categoryDetails!.businessPercent), 2);
  }
  return '';
}

export function formattedLocalAmount(t: LiliTransaction): string {
  if (t.localAmount) {
    return convertValueToUSD(t.localAmount, 2);
  }
  return '';
}

export function formattedAuthDelta(t: LiliTransaction): string {
  if (t.authDelta) {
    return convertValueToUSD(t.authDelta, 2);
  }
  return '';
}

export function isCategorized(t: LiliTransaction): boolean {
  return t.categoryDetails?.businessPercent != null;
}

export function isCategoryDetailsExist(t: LiliTransaction): boolean {
  return !!t.categoryDetails?.categoryExternalId;
}

export function isCategorizedAsBusiness(t: LiliTransaction): boolean {
  return (t.categoryDetails?.businessPercent ?? 0) > 0;
}

export function isCategorizedMostlyAsBusiness(t: LiliTransaction): boolean {
  return (t.categoryDetails?.businessPercent ?? 0) > 0.5;
}

export function isCategorizedOnlyAsBusiness(t: LiliTransaction): boolean {
  return (t.categoryDetails?.businessPercent ?? 0) === 1;
}

export function isCategorizedAsSplitted(t: LiliTransaction): boolean {
  return isCategorizedAsBusiness(t) && !isCategorizedOnlyAsBusiness(t);
}

export function canBeCategorizedRegardlessFeatureFlag(t: LiliTransaction): boolean {
  return t.allowCategorizationEnum === TransactionAllowCategorizationEnum.READ_WRITE;
}

export function canBeCategorized(t: LiliTransaction): boolean {
  if (canBeCategorizedRegardlessFeatureFlag(t) && validateFeatureFlag(FEATURE_FLAG.CATEGORIZATION.FEATURE)) {
    if (t.categorizationType === TransactionCategorizationType.INCOME) {
      return validateFeatureFlag(FEATURE_FLAG.CATEGORIZATION.INCOME);
    }
    return true;
  }
  return false;
}

export function canAttachReceipt(t: LiliTransaction): boolean {
  return canBeCategorized(t);
}

export function hasReceipt(t: LiliTransaction): boolean {
  return t.receiptKey != null;
}

export function hasNote(t: LiliTransaction): boolean {
  return t.comment != null;
}
